import { Injectable } from '@angular/core';

import { HubspotFormTypeEnum } from '../enums/hubspot-form-type.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { ConfigService } from '../../../Core/Config/services/config.service';

/**
 * Wrapper for hubspot forms library, call through submitForm with the enum value and it will make a call to hubspot with the appropriate
 * information for the current user
 */
@Injectable()
export class HubspotFormService {

    private static readonly formId: string = '37443dfa-f584-4c96-a21a-a2d6e2b018a6';

    private scriptLoaded: boolean = false;

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private configService: ConfigService
    ) {}

    /**
     * Requests access to a locked feature: white-labeled brands get a mailto link instead of the
     * normal hubspot form, since they have their own sales contact instead of Agility's.
     *
     * @returns true if the hubspot form was submitted (caller should show a confirmation), false if
     * a mailto redirect was used instead (nothing further for the caller to do)
     */
    async requestUnlock(hubspotFormType: HubspotFormTypeEnum, featureName: string): Promise<boolean> {
        if (this.configService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
            return false;
        }
        if (this.configService.brandGUIDIsPaMedia()) {
            window.location.href = 'mailto:salesadminteam@pamediagroup.com?subject=Urgent%3A%20Agility%20Feature%20Upgrade%20Request&body='
                + this.upgradeRequestBody(featureName);
            return false;
        }
        if (this.configService.brandGUIDIsRefMedia()) {
            window.location.href = 'mailto:contact@refmedia.ca?subject=Urgent%3A%20Agility%20Feature%20Upgrade%20Request&body='
                + this.upgradeRequestBody(featureName);
            return false;
        }
        await this.submitForm(hubspotFormType);
        return true;
    }

    private upgradeRequestBody(featureName: string): string {
        return encodeURIComponent(
            `Hello,\n\nI'm interested in upgrading to include the "${featureName}" feature in my plan.`
        );
    }

    async submitForm(formType: HubspotFormTypeEnum): Promise<void> {
        const hubSpotForms = await this.getHubspotForms();
        hubSpotForms.create({
            region: 'na1',
            portalId: '469676',
            formId: HubspotFormService.formId
        });
        return new Promise(resolve => {
            hubSpotForms.onFormReady((form) => {
                const currentUserSubscription = this.currentUserStateService.getUser().subscribe((user) => {
                    const firstNameField = document.getElementById('firstname-' + HubspotFormService.formId) as HTMLInputElement;
                    firstNameField.value = user.firstName;
                    const lastNameField = document.getElementById('lastname-' + HubspotFormService.formId) as HTMLInputElement;
                    lastNameField.value = user.lastName;
                    const emailField = document.getElementById('email-' + HubspotFormService.formId) as HTMLInputElement;
                    emailField.value = user.username;
                    const typeFields = document.querySelectorAll(`[id^="upsell_inquiry"][id$="${HubspotFormService.formId}"]`);
                    typeFields.forEach(field => {
                        if (field) {
                            (field as HTMLInputElement).checked = false;
                        }
                    });
                    const typeField = document.getElementById('upsell_inquiry' + formType + '-' + HubspotFormService.formId) as HTMLInputElement;
                    typeField.checked = true;
                    const formDom = document.getElementById('hsForm_' + form.id) as HTMLFormElement;
                    fetch(
                        formDom.action,
                        {
                            method: formDom.method,
                            body: new FormData(formDom)
                        }
                    ).then((response) => {
                        hubSpotForms.removeAll();
                        currentUserSubscription.unsubscribe();
                        resolve();
                    });
                });
            });
        });
    }

    private async getHubspotForms(): Promise<any> {
        if (!this.scriptLoaded) {
            await this.injectScript();
        }
        return (window as any).HubSpotForms;
    }

    private injectScript(): Promise<void> {
        const script = document.createElement('script');

        script.src = '//js.hsforms.net/forms/embed/v2.js';
        script.type = 'text/javascript';
        script.charset = 'utf-8';
        script.async = true;

        document.head.appendChild(script);

        this.scriptLoaded = true;

        return new Promise(
            resolve => {
                script.addEventListener('load', () => {
                    resolve();
                });
            }
        );
    }
}
